// Library Methods
import { useCallback } from "react";

// Components
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import { Breakpoint } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CloseIcon } from "src/assets/icons";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

type ExamCardModalType = {
  isOpen: boolean;
  onClose: () => void;
  title: string | JSX.Element;
  children: React.ReactNode;
  fullScreen?: boolean;
  maxWidth?: Breakpoint;
  modalActions?: JSX.Element;
  extraHeaderButtons?: Array<JSX.Element>;
  hideHeaderButtons?: boolean;
  backgroundColor?: string;
};

// Same shell as DefaultModal, just with 8px header/footer padding instead of
// DefaultModal's -- used by the AddEditIOLModal/AddEditProgressiveModal exam
// card editors, which want a denser header/footer than the rest of the app.
const ExamCardModal = ({
  isOpen,
  onClose,
  title,
  children,
  fullScreen = false,
  maxWidth = "sm",
  modalActions = undefined,
  extraHeaderButtons = undefined,
  hideHeaderButtons = false,
  backgroundColor = undefined,
}: ExamCardModalType) => {
  const theme = useTheme();
  const { surface, onSurface } = getSurfaceTokens(theme);

  // don't close the modal when clicking the backdrop
  const handleClose = useCallback(
    (_?: {}, reason?: string) => {
      if (reason !== "backdropClick") onClose();
    },
    [onClose]
  );

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth={maxWidth}
      disableRestoreFocus
      PaperProps={{
        sx: {
          ...(theme.palette.mode === "dark" && {
            backgroundColor: "#5C6076",
            backgroundImage: "none",
          }),
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#202338",
          color: "#FFF",
          padding: "8px",
        }}
      >
        <Box fontWeight={600} fontSize="14px" maxWidth="90%">
          {title}
        </Box>

        <Box sx={{ display: hideHeaderButtons ? "none" : "flex", gap: "8px" }}>
          {extraHeaderButtons?.map((extraButton, index) => (
            <div key={`extra-button-${index}`}>{extraButton}</div>
          ))}
          <IconButton
            disableFocusRipple
            disableTouchRipple
            disableRipple
            aria-label="close-modal"
            color="inherit"
            onClick={handleClose}
            sx={{ p: 0 }}
          >
            <CloseIcon height="20px" width="20px" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 0 }}>{children}</DialogContent>
      {modalActions && (
        <DialogActions
          sx={{
            backgroundColor:
              backgroundColor ??
              (theme.palette.mode === "dark" ? "#5C6076" : surface),
            color: backgroundColor ? "inherit" : onSurface,
            padding: "8px",
          }}
        >
          {modalActions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ExamCardModal;
